// ═══════════════════════════════════════════════════════════════
// RichTextEditor — Tiptap-based rich text editor
// Used for Scope of Work, Damage Description, and other long-form fields
// ═══════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

// Toolbar button style
const btn = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 4, border: 'none', cursor: 'pointer',
  background: active ? '#4B3FFF' : 'transparent',
  color: active ? '#fff' : '#374151',
  fontWeight: 600, fontSize: 13,
  transition: 'background 0.1s',
});

const sep: React.CSSProperties = {
  width: 1, height: 18, background: '#e2e8f0', margin: '0 4px', flexShrink: 0,
};

export default function RichTextEditor({ value, onChange, placeholder = 'Start typing…', minHeight = 280 }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync value in when parent changes it externally (e.g. reset)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value || '', false);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;

  return (
    <div style={{ border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
        padding: '6px 10px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc',
      }}>
        {/* History */}
        <button style={btn(false)} title="Undo" onClick={() => editor.chain().focus().undo().run()}>↩</button>
        <button style={btn(false)} title="Redo" onClick={() => editor.chain().focus().redo().run()}>↪</button>
        <div style={sep} />

        {/* Headings */}
        <button style={btn(editor.isActive('heading', { level: 1 }))} title="Heading 1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</button>
        <button style={btn(editor.isActive('heading', { level: 2 }))} title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
        <button style={btn(editor.isActive('heading', { level: 3 }))} title="Heading 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</button>
        <div style={sep} />

        {/* Inline formatting */}
        <button style={{ ...btn(editor.isActive('bold')), fontWeight: 800 }} title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}>B</button>
        <button style={{ ...btn(editor.isActive('italic')), fontStyle: 'italic' }} title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}>I</button>
        <button style={{ ...btn(editor.isActive('underline')), textDecoration: 'underline' }} title="Underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}>U</button>
        <button style={{ ...btn(editor.isActive('strike')), textDecoration: 'line-through' }} title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}>S</button>
        <div style={sep} />

        {/* Lists */}
        <button style={btn(editor.isActive('bulletList'))} title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}>• —</button>
        <button style={btn(editor.isActive('orderedList'))} title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</button>
        <div style={sep} />

        {/* Block */}
        <button style={btn(editor.isActive('blockquote'))} title="Blockquote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>&ldquo;</button>
        <button style={btn(editor.isActive('codeBlock'))} title="Code block"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}>{'{}'}</button>
        <button style={btn(false)} title="Horizontal rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}>—</button>
        <div style={sep} />

        {/* Alignment */}
        <button style={btn(editor.isActive({ textAlign: 'left' }))} title="Align left"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}>⬡L</button>
        <button style={btn(editor.isActive({ textAlign: 'center' }))} title="Align center"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}>⬡C</button>
        <button style={btn(editor.isActive({ textAlign: 'right' }))} title="Align right"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}>⬡R</button>
        <div style={sep} />

        {/* Clear */}
        <button style={btn(false)} title="Clear formatting"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>✕</button>
      </div>

      {/* Editor area */}
      <EditorContent
        editor={editor}
        style={{ minHeight, padding: '12px 16px', fontSize: 14, lineHeight: 1.7, cursor: 'text' }}
      />

      {/* Character count */}
      <div style={{ padding: '4px 12px', background: '#f8fafc', borderTop: '1px solid #f1f5f9',
        fontSize: 11, color: '#94a3b8', textAlign: 'right' }}>
        {editor.storage.characterCount?.characters?.() ?? editor.getText().length} characters
      </div>

      <style>{`
        .tiptap { outline: none; }
        .tiptap p { margin: 0 0 8px 0; }
        .tiptap h1 { font-size: 1.4em; font-weight: 700; margin: 12px 0 6px; }
        .tiptap h2 { font-size: 1.2em; font-weight: 700; margin: 10px 0 5px; }
        .tiptap h3 { font-size: 1.05em; font-weight: 600; margin: 8px 0 4px; }
        .tiptap ul, .tiptap ol { padding-left: 20px; margin: 4px 0 8px; }
        .tiptap li { margin-bottom: 2px; }
        .tiptap blockquote { border-left: 3px solid #4B3FFF; padding-left: 12px; color: #555; margin: 8px 0; }
        .tiptap code { background: #f1f5f9; border-radius: 3px; padding: 1px 4px; font-size: 0.9em; }
        .tiptap pre { background: #1e293b; color: #e2e8f0; padding: 12px 16px; border-radius: 6px; overflow-x: auto; margin: 8px 0; }
        .tiptap pre code { background: none; color: inherit; padding: 0; }
        .tiptap hr { border: none; border-top: 1px solid #e2e8f0; margin: 12px 0; }
        .tiptap a { color: #4B3FFF; text-decoration: underline; }
        .tiptap p.is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #adb5bd; pointer-events: none; height: 0; }
      `}</style>
    </div>
  );
}
